import appConfig from "../../appconfig";
import { store } from "../../store";
import { BifrostBackendApi, BifrostBackendNestApi } from "../../gateways/BifrostBackendApi";

const bifrostBackend = (useNewEndPointForcefully = false) => {
	const useNewSubscriptionEndPoint = store.getters.subscriptionExistsData.useNewSubscriptionEndPoint || useNewEndPointForcefully;
	return useNewSubscriptionEndPoint ? BifrostBackendNestApi : BifrostBackendApi;
};

const SubscriptionProvider = {
	async subscriptionExists(idpToken) {
		const idpManagementQueryParams = {
			tenantId: appConfig.tenantConfig.tenantId
		};
		return BifrostBackendNestApi.get("/subscriptions/exists", {
			params: idpManagementQueryParams,
			headers: {
				Authorizationidp: idpToken
			}
		}).then(({ data }) => data);
	},
	async initializeSubscription(idpToken, countryCode, urlAccept, urlCancel, urlCheckout, planId, additionalInvoiceDetails, additionalPaymentDetails, deviceData) {
		const clientTraceIdFromStorage = localStorage.getItem("clientTraceId");

		const isMobile = appConfig.mobile.isMobileApp();
		const platform = appConfig.mobile.mobileApi().platformId;
		const baseUrl = isMobile ? appConfig.getPaymentUrl() : window.location.origin;
		const urlRedirectHelper = `${baseUrl}/redirect-helper`;

		const initializeSubscriptionBody = {
			tenantId: appConfig.tenantId,
			countryCode,
			urlAccept,
			urlCancel,
			urlCheckout,
			...(platform !== "android" && { urlRedirectHelper } ),
			planId,
			additionalInvoiceDetails,
			additionalPaymentDetails,
			clientTraceId: clientTraceIdFromStorage ? clientTraceIdFromStorage : "",
			deviceData
		};

		// for new subscriptions, we will always use the new v2 end point
		// to make sure subscription provider is billwerkplus
		return BifrostBackendNestApi.post(
			`/subscriptions/initialize`,
			initializeSubscriptionBody,
			{
				headers: {
					Authorizationidp: idpToken
				}
			})
			.then(({ data }) => data);
	},
	// TODO
	// the endpoint in nest should be updated to eg /subscriptions/update-payment-method
	async updateSubscriptionPaymentMethod(idpToken, urlAccept, urlCancel, urlCheckout, planId, paymentMethod, additionalPaymentDetails) {
		const isMobile = appConfig.mobile.isMobileApp();
		const baseUrl = isMobile ? appConfig.getPaymentUrl() : window.location.origin;
		const urlRedirectHelper = `${baseUrl}/redirect-helper`;

		const updateSubscriptionPaymentMethodBody = {
			tenantId: appConfig.tenantId,
			urlAccept,
			urlCancel,
			urlCheckout,
			urlRedirectHelper,
			planId,
			paymentMethod,
			additionalPaymentDetails
		};

		return bifrostBackend().put(
			`/subscriptions/initialize/update`,
			updateSubscriptionPaymentMethodBody,
			{
				headers: {
					Authorizationidp: idpToken
				}
			})
			.then(({ data }) => data);
	},
	async activateSubscriptionPlan(token, body) {
		return BifrostBackendApi.post(
			`/subscriptions/activate-plan`,
			{
				tenantId: appConfig.tenantId,
				...body
			},
			{
				headers: {
					Authorizationidp: token
				}
			})
			.then(({ data }) => data);
	},
	async updateSubscriptionPlan(token, body) {
		return BifrostBackendApi.post(
			`/subscriptions/update-plan`,
			{
				tenantId: appConfig.tenantId,
				...body
			},
			{
				headers: {
					Authorizationidp: token
				}
			})
			.then(({ data }) => data);
	},
	async listSubscriptionPlans() {
		return BifrostBackendNestApi.get(
			`/subscriptions/plans?tenantId=${appConfig.tenantId}`
		).then(({ data }) => data);
	},
	async listInvoicesByCustomer(token) {
		return bifrostBackend().get(
			`/subscriptions/invoices?tenantId=${appConfig.tenantId}`, {
				headers: {
					Authorizationidp: token
				}
			}
		).then(({ data }) => data);
	},
	async getInvoicePdfById(token, invoiceId, isCreditNote = false) {
		return bifrostBackend().get(
			`/subscriptions/invoices/${invoiceId}/download?tenantId=${appConfig.tenantId}${isCreditNote ? "&isCreditNote=1" : ""}`, {
				responseType: "blob",
				headers: {
					Authorizationidp: token
				}
			}
		).then(( response ) => {
			return {
				pdfData: response.data,
				fileName: response.headers["content-disposition"].split("filename=")[1].split(";")[0].replace(/"/g, "")
			};
		});
	},
	async getSubscriptionPlanById(planId) {
		return bifrostBackend().get(
			`/subscriptions/plans/${planId}?tenantId=${appConfig.tenantId}`
		).then(({ data }) => data);
	},
	async cancelSubscription(token, shopId) {
		// TODO
		// I noticed that we are not using the shopId or the subscriptionConfig in the params provided.
		// may be at sometime it was being used but with the current implementation I did not see these values
		// being used. If this is the case, when we shift to billwerk completely, we need to remove the shopId
		// and the subscriptionConfig url params.
		return bifrostBackend().post(
			`/subscriptions/cancel?tenantId=${appConfig.tenantId}${shopId ? `&shopId=${shopId}` : ""}`,
			{
				subscriptionConfig: {
					canceloption: 2
				}
			},
			{
				headers: {
					Authorizationidp: token
				}
			}
		).then(({ data }) => data);
	},
	async switchSubscriptionPlan(token, switchSubscriptionPlanConfig) {
		return bifrostBackend().post(
			`/subscriptions/switch-plan?tenantId=${appConfig.tenantId}`,
			{
				config: switchSubscriptionPlanConfig
			},
			{
				headers: {
					Authorizationidp: token
				}
			}
		).then(({ data }) => data);
	},
	async getPaymentMethodStatus(token, shopId) {
		return BifrostBackendApi.get(
			`/subscriptions/payment-methods`,
			{
				params: {
					tenantId: appConfig.tenantId,
					shopId
				},
				headers: {
					Authorizationidp: token
				}
			}
		).then(({ data }) => data);
	},
	/**
	 * TODO
	 * We can remove this once billwerk is completely implemented.
	 */
	async saveNetsPayment({ authorization, tenantId, customerId, planId, paymentid }) {
		if(!authorization || !tenantId || !customerId || !planId || !paymentid) {
			return;
		}

		return BifrostBackendApi.post("/subscriptions/webhooks/nets",
			{ paymentId: paymentid },
			{
				params: {
					tenantId,
					customerId,
					planId
				}, headers: {
					authorization
				}
			}
		).then(({ data }) => data);
	},

	async uncancelSubscription(token, shopId) {
		return bifrostBackend().post(
			`/subscriptions/uncancel?tenantId=${appConfig.tenantId}${shopId ? `&shopId=${shopId}` : ""}`,
			{},
			{
				headers: {
					Authorizationidp: token
				}
			}
		).then(({ data }) => data);
	}
};

export default SubscriptionProvider;
